import React, { useEffect, useState } from 'react';
import './Modal.css';
import { Validation } from '../../utils/validation';

const defaultAddressForm = {
    nick_name: '',
    contact_name: '',
    address: '',
    address_2: '',
    city: '',
    zip: '',
    state: '',
    country: 'United State',
    phone: '',
    email: '',
    address_type: 'shipping',
};

const SaveAddressModal = ({
    show,
    shippingDraft,
    editingAddress,
    addressType = 'shipping',
    onClose,
    onSaveAddress,
    company_name,
    company_id
}) => {
    const [formData, setFormData] = useState(defaultAddressForm);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!show) return;
        if (editingAddress) {
            setFormData({
                id: editingAddress.id || editingAddress.address_id || null,
                nick_name: editingAddress.nick_name || '',
                contact_name: editingAddress.contact_name || '',
                address: editingAddress.address || '',
                address_2: editingAddress.address_2 || '',
                city: editingAddress.city || '',
                zip: editingAddress.zip || '',
                state: editingAddress.state || '',
                country: editingAddress.country || 'United State',
                phone: editingAddress.phone || '',
                email: editingAddress.email || '',
                address_type: editingAddress.address_type || addressType || 'shipping',
            });
        } else {
            setFormData({
                ...defaultAddressForm,
                address: shippingDraft || '',
                address_type: addressType || 'shipping',
                country: 'United State',
            });
        }
        setErrors({});
    }, [show, shippingDraft, editingAddress, addressType]);

    if (!show) return null;

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSave = (event) => {
        const normalized = {
            ...formData,
            nick_name: formData.nick_name?.trim() || '',
            contact_name: formData.contact_name?.trim() || '',
            address: formData.address?.trim() || '',
            address_2: formData.address_2?.trim() || '',
            city: formData.city?.trim() || '',
            zip: formData.zip?.trim() || '',
            state: formData.state?.trim() || '',
            country: formData.country?.trim() || 'United State',
            phone: formData.phone?.trim() || '',
            email: formData.email?.trim() || '',
            address_type: formData.address_type || addressType || 'shipping',
        };

        // Validate required fields
        const validationFields = ['nick_name', 'address_2', 'address', 'city', 'state', 'zip'];
        const validationErrors = Validation(normalized, validationFields);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setErrors({});

        // Include company_name if provided via props
        const payload = {
            ...normalized,
            company_name: company_name || '',
            company_id: company_id || 0,
        };

        if (formData.id) {
            payload.id = formData.id;
            payload.address_id = formData.id;
        }

        if (typeof onSaveAddress === 'function') {
            onSaveAddress(payload, event);
        }
    };

    const isEditMode = Boolean(formData.id);
    const isShippingType = formData.address_type === 'shipping';

    const getModalTitle = () => {
        if (isEditMode) {
            return isShippingType ? 'Edit Shipping Address' : 'Edit Main Office Address';
        }
        return isShippingType ? 'Save Shipping Address' : 'Save Main Office Address';
    };

    return (
        <div className="motor-modal-overlay" onClick={onClose}>
            <div
                className="motor-modal-content"
                style={{ maxWidth: '560px', width: '90%', padding: 0 }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="modal-header-dark">
                    <h4 className="m-0">
                        {getModalTitle()}
                    </h4>
                    <button
                        type="button"
                        className="btn-close btn-close-white"
                        onClick={onClose}
                        aria-label="Close"
                    ></button>
                </div>

                {/* Modal Body */}
                <div className="modal-body-content" style={{ padding: '20px' }}>
                    <div className="border rounded p-3 bg-light">
                        <div className="d-flex flex-column gap-3">
                            {isShippingType ? (
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Nick Name</label>
                                        <input type="text" className="form-control" name="nick_name" value={formData.nick_name} onChange={handleChange} placeholder="Home / Office / Main" />
                                        {errors.nick_name && <div className="text-danger small mt-1">{errors.nick_name}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Contact Name</label>
                                        <input type="text" className="form-control" name="contact_name" value={formData.contact_name} onChange={handleChange} placeholder="John Doe" />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label className="form-label fw-semibold">Nick Name</label>
                                    <input type="text" className="form-control" name="nick_name" value={formData.nick_name} onChange={handleChange} placeholder="Home / Office / Main" />
                                </div>
                            )}
                            <div>
                                <label className="form-label fw-semibold">Street Address 1</label>
                                <textarea className="form-control" rows="2" name="address" value={formData.address} onChange={handleChange} placeholder="123 Main Street" />
                                {errors.address && <div className="text-danger small mt-1">{errors.address}</div>}
                            </div>
                            <div>
                                <label className="form-label fw-semibold">Street Address 2 (Optional)</label>
                                <input type="text" className="form-control" name="address_2" value={formData.address_2} onChange={handleChange} placeholder="Suite 100 / Building B" />
                                {errors.address_2 && <div className="text-danger small mt-1">{errors.address_2}</div>}
                            </div>
                            <div className="row g-2">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">City</label>
                                    <input type="text" className="form-control" name="city" value={formData.city} onChange={handleChange} />
                                    {errors.city && <div className="text-danger small mt-1">{errors.city}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">State</label>
                                    <input type="text" className="form-control" name="state" value={formData.state} onChange={handleChange} />
                                    {errors.state && <div className="text-danger small mt-1">{errors.state}</div>}
                                </div>
                            </div>
                            <div className="row g-2">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Postal Code / Zip</label>
                                    <input type="text" className="form-control" name="zip" value={formData.zip} onChange={handleChange} />
                                    {errors.zip && <div className="text-danger small mt-1">{errors.zip}</div>}
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Country</label>
                                    <input type="text" className="form-control" name="country" value={formData.country} onChange={handleChange} />
                                </div>
                            </div>
                            {isShippingType && (
                                <div className="row g-2">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Phone Number</label>
                                        <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 000-0000" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Email (Optional)</label>
                                        <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} placeholder="contact@company.com" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="modal-footer-dark">
                    <button type="button" className="btn btn-secondary px-4" onClick={onClose}>
                        Close
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary px-4"
                        style={{ backgroundColor: '#bb5d40', borderColor: '#bb5d40' }}
                        onClick={handleSave}
                    >
                        {isEditMode ? 'Update Address' : 'Save Address'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SaveAddressModal;
